import React, { useState, useMemo, useEffect } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Search, X, Eye, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

// Highlight search term(s) inside a plain-text snippet (XSS-safe).
function Highlighted({ text, terms }) {
  if (!text) return null;
  const cleaned = (terms || []).map((t) => (t || "").trim()).filter(Boolean);
  if (!cleaned.length) return <>{text}</>;
  const escaped = cleaned.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "ig");
  const parts = String(text).split(re);
  const lowered = new Set(cleaned.map((t) => t.toLowerCase()));
  return (
    <>
      {parts.map((part, i) =>
        lowered.has(part.toLowerCase()) ? (
          <mark key={i} className="rounded bg-yellow-200 px-0.5 text-slate-900">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

export default function ESCRJudgmentList() {
  const location = useLocation();
  const navigate = useNavigate();
  const initial = location.state;

  const [search] = useState(initial?.search || null);
  const allResults = useMemo(() => initial?.data?.results || [], [initial]);
  const total = initial?.data?.totalRecords ?? allResults.length;

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [query, setQuery] = useState("");

  // Filter the FULL result set (not just the current page).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allResults;
    return allResults.filter((r) =>
      [r.caseNumber, r.caseTitle, r.cnr, r.judgeName, r.snippet, r.courtName]
        .some((f) => typeof f === "string" && f.toLowerCase().includes(q))
    );
  }, [allResults, query]);

  const filteredTotal = filtered.length;
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize));

  // If the filtered set shrinks below the current page, snap back.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  // Open the judgment on its own page (with the Export-to-Case button).
  const openJudgment = (row) => {
    if (!row.pdfUrl) return toast.error("No PDF available for this judgment.");
    navigate("/portal/escr/judgment", { state: { row, search } });
  };

  const clearSearch = () => {
    setQuery("");
    setPage(1);
  };

  const goToPage = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Compact window of page numbers around the current page.
  const getPageNumbers = () => {
    const nums = [];
    const maxPagesToShow = 5;
    let start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + maxPagesToShow - 1);
    if (end - start < maxPagesToShow - 1) start = Math.max(1, end - maxPagesToShow + 1);
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  };

  if (!search) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <main className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="mt-4">
            <FindCaseStepper current={3} />
          </div>
          <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white py-12 text-center shadow-sm">
            <p className="text-base font-medium text-slate-700">No search results to display.</p>
            <Link
              to="/portal/fetchfromescr"
              className="mt-4 inline-flex h-10 items-center rounded-lg bg-yellow-500 px-6 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400"
            >
              Go to eSCR Search
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const first = (page - 1) * pageSize;
  const last = page * pageSize;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="mb-4 flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">
            Find Cases
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <Link to="/portal/fetchfromescr" className="text-slate-500 hover:text-slate-800">
            eSCR Judgment &amp; Order
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Results</span>
        </div>

        {/* Stepper */}
        <div className="mb-4">
          <FindCaseStepper current={3} />
        </div>

        {/* Summary stat card */}
        <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500">
              <Users className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-bold leading-tight text-slate-900">{total.toLocaleString()}</p>
              <p className="truncate text-xs text-slate-500">Total Found</p>
            </div>
          </div>
        </div>

        {/* Keyword context */}
        <p className="mb-3 text-sm text-slate-500">
          About <span className="font-semibold text-slate-900">{total.toLocaleString()}</span> results for{" "}
          <span className="font-medium text-slate-800">&ldquo;{search.keyword}&rdquo;</span>
          {query.trim() && (
            <span>
              {" "}
              &middot; {filteredTotal.toLocaleString()} match{filteredTotal === 1 ? "" : "es"}
            </span>
          )}
        </p>

        {/* Results card */}
        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Toolbar — search within results */}
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Search in results..."
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
              />
              {query && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          {pageRows.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-base font-medium text-slate-700">
                {query.trim() ? "No judgments match your search" : "No judgments found"}
              </p>
              {query.trim() && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <ol className="divide-y divide-slate-100">
              {pageRows.map((r, idx) => (
                <li
                  key={`${r.cnr}-${idx}`}
                  className="flex items-start gap-3 p-4 transition-colors duration-150 hover:bg-slate-50"
                >
                  <span className="w-6 shrink-0 pt-0.5 text-sm font-medium text-slate-400">
                    {first + idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => openJudgment(r)}
                      className="text-left text-[15px] font-semibold leading-snug text-slate-900 transition-colors hover:text-yellow-600 hover:underline"
                    >
                      {[r.caseNumber, r.caseTitle].filter(Boolean).join(" — ") || "—"}
                    </button>
                    {r.judgeName && (
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Judge: <span className="font-normal normal-case text-slate-600">{r.judgeName}</span>
                      </p>
                    )}
                    {r.snippet && (
                      <p className="mt-1.5 line-clamp-3 text-sm leading-relaxed text-slate-600">
                        <Highlighted
                          text={r.snippet}
                          terms={r.highlightedKeywords?.length ? r.highlightedKeywords : [search.keyword]}
                        />
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                      {r.cnr && (
                        <span>
                          CNR: <span className="font-medium text-slate-700">{r.cnr}</span>
                        </span>
                      )}
                      {r.registrationDate && (
                        <span>
                          Reg: <span className="font-medium text-slate-700">{r.registrationDate}</span>
                        </span>
                      )}
                      {r.decisionDate && (
                        <span>
                          Decision: <span className="font-medium text-slate-700">{r.decisionDate}</span>
                        </span>
                      )}
                      {r.disposalNature && r.disposalNature !== "N/A" && (
                        <span>
                          Disposal: <span className="font-medium text-slate-700">{r.disposalNature}</span>
                        </span>
                      )}
                    </div>
                    {r.courtName && <p className="mt-1 text-xs italic text-slate-400">Court: {r.courtName}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => openJudgment(r)}
                    title="Open judgment"
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ol>
          )}

          {/* Footer — result range + pagination */}
          {filteredTotal > 0 && (
            <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
              <p className="text-sm text-slate-500">
                Showing {first + 1} to {Math.min(last, filteredTotal)} of {filteredTotal} results
              </p>

              {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {page > 3 && (
                    <>
                      <button
                        onClick={() => goToPage(1)}
                        className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                      >
                        1
                      </button>
                      {page > 4 && <span className="px-1 text-slate-400">...</span>}
                    </>
                  )}

                  {getPageNumbers().map((number) => (
                    <button
                      key={number}
                      onClick={() => goToPage(number)}
                      className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                        page === number
                          ? "bg-yellow-500 text-slate-900"
                          : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {number}
                    </button>
                  ))}

                  {page < totalPages - 2 && (
                    <>
                      {page < totalPages - 3 && <span className="px-1 text-slate-400">...</span>}
                      <button
                        onClick={() => goToPage(totalPages)}
                        className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page === totalPages}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
